import { z } from "zod";

/**
 * İMZA TALEBİ (CLOS-164 `[157]`).
 *
 * Talep, imzanın kendisi değil: hangi versiyon için, hangi uygunluk kuralıyla imza
 * sürecinin başlatıldığını söyleyen kayıt. İmzacılar, sıra, durum ve gerçek imza
 * `[158]`–`[160]` kartlarında.
 */

/**
 * İmza öncesi UYGUNLUK politikaları (kartın AÇIK KARARI).
 *
 * ⚠ `NONE` da bir POLİTİKADIR, politikasızlık değil: "bu organizasyon imza için önceden
 * onay aramıyor" kararı da açıkça verilmiş olmalı. Varsayılanı "kural yok" bırakmak,
 * kimsenin seçmediği bir davranışı ürünün varsayılanı yapardı.
 */
export const SIGNATURE_ELIGIBILITY_POLICIES = [
  "NONE",
  "VALID_APPROVAL",
  "VALID_CONSENSUS",
] as const;
export type SignatureEligibilityPolicy = (typeof SIGNATURE_ELIGIBILITY_POLICIES)[number];

export const SIGNATURE_ELIGIBILITY_POLICY_NAMES: Record<SignatureEligibilityPolicy, string> = {
  NONE: "Ön koşul aranmıyor",
  VALID_APPROVAL: "Geçerli iç onay gerekli",
  VALID_CONSENSUS: "Geçerli taraf mutabakatı gerekli",
};

export const SIGNING_MODES = ["serial", "parallel"] as const;
export type SigningMode = (typeof SIGNING_MODES)[number];

export const SIGNATURE_METHODS = ["wet_ink", "qualified_certificate", "in_person"] as const;
export type SignatureMethod = (typeof SIGNATURE_METHODS)[number];

/** Kullanıcıya görünen adlar ŞEMADA: arayüz kendi sözlüğünü uydurmuyor (kriter 4). */
export const SIGNATURE_METHOD_NAMES: Record<SignatureMethod, string> = {
  wet_ink: "Islak imza",
  qualified_certificate: "Nitelikli elektronik sertifika",
  in_person: "Yüz yüze",
};

export const contractSignatureConfigSchema = z.object({
  version: z.literal(3),
  /** Desteklenen imza SIRASI türleri (CLOS-165 `[158]` açık kararı). */
  signingModes: z.array(z.enum(SIGNING_MODES)).min(1),
  /** Tek talepte azami imzacı. */
  maxSigners: z.number().int().min(1),
  /**
   * İmzacının iletişim kanalı DOĞRULANMIŞ olmalı mı (kartın açık kararı). `true` iken
   * e-postası doğrulanmamış kişi imzacı olamıyor — imza bildirimi doğrulanmamış bir
   * adrese gitseydi, imzayı kimin attığı tartışmalı olurdu.
   */
  requireVerifiedContact: z.boolean(),
  /**
   * Bu kurulumda KULLANILABİLİR politikalar. Kod bilinmeyen bir politikayı açılışta
   * reddediyor; listede olmayan bir politika istenirse istek reddediliyor — kullanıcı
   * politikayı sessizce bypass edemiyor (kartın açık kuralı).
   */
  allowedPolicies: z.array(z.enum(SIGNATURE_ELIGIBILITY_POLICIES)).min(1),
  /** İstek politikayı belirtmezse uygulanan. */
  defaultPolicy: z.enum(SIGNATURE_ELIGIBILITY_POLICIES),
  /**
   * E-imza adapter parametreleri (CLOS-167 `[160]`, ADR-0035). Sağlayıcı SEÇİLMEDİ;
   * adapter jenerik HMAC-webhook.
   */
  provider: z.object({
    /**
     * Zaman damgası penceresi. Dar tutmak replay yüzeyini küçültür; çok dar tutmak
     * sağlayıcının meşru gecikmesini reddeder.
     */
    webhookToleranceSeconds: z.number().int().min(30).max(3600),
  }),
  /** Manuel (ıslak) imza kabul politikası — sağlayıcı sonucunu TAKLİT ETMEZ. */
  manualSignature: z.object({
    allowed: z.boolean(),
    /** Kanıt dosyası zorunlu mu: sistemin doğrulayamadığı beyanın dayanağı durmalı. */
    requireEvidence: z.boolean(),
    allowedMethods: z.array(z.enum(SIGNATURE_METHODS)).min(1),
  }),
  /** İmzalı belge arşivi sınırları. */
  artifact: z.object({
    maxBytes: z.number().int().positive(),
    allowedContentTypes: z.array(z.string().min(1)).min(1),
  }),
});
export type ContractSignatureConfig = z.infer<typeof contractSignatureConfigSchema>;

export const SIGNING_MODE_NAMES: Record<SigningMode, string> = {
  serial: "Sırayla (seri)",
  parallel: "Aynı anda (paralel)",
};

export const createSignatureRequestSchema = z.strictObject({
  versionId: z.uuid("Geçerli bir sözleşme versiyonu seçilmelidir"),
  /** Verilmezse konfigdeki varsayılan uygulanır. */
  eligibilityPolicy: z.enum(SIGNATURE_ELIGIBILITY_POLICIES).optional(),
  /** İmza sırası; verilmezse `parallel`. */
  signingMode: z.enum(SIGNING_MODES).optional(),
  /**
   * İmza süresi (CLOS-166 `[159]`).
   *
   * ⚠ Son tarih ZORUNLU (ADR-0065, Rapor 5 §5) — süreç ölçülebilir gecikme taşımak
   * zorunda; eski satırlar null kalır.
   */
  expiresAt: z.iso.datetime({ message: "Geçerli bir son tarih girin" }),
  description: z.string().trim().max(2000, "Açıklama en fazla 2000 karakter olabilir").optional(),
});

/**
 * İMZACI PLANI (CLOS-165 `[158]`).
 *
 * ⚠ Plan TOPLUCA veriliyor: tek tek ekleme/çıkarma uçları yerine "planın son hâli" tek
 * komutta yazılıyor. Sıra ve zorunluluk birbirine bağlı kararlar; parça parça
 * güncellenseydi arada tutarsız bir plan (iki kişi aynı sırada, hiç zorunlu imzacı yok)
 * kaydedilebilirdi.
 */
export const setContractSignersSchema = z.strictObject({
  signers: z
    .array(
      z.strictObject({
        partyId: z.uuid("Geçerli bir sözleşme tarafı seçilmelidir"),
        signerUserId: z.uuid("Geçerli bir kullanıcı seçilmelidir"),
        signerTenantId: z.uuid("Geçerli bir çalışma alanı seçilmelidir"),
        /** Verilmezse zorunlu: bilerek eklenen bir imzacı varsayılan olarak gereklidir. */
        required: z.boolean().optional(),
      }),
    )
    .min(1, "En az bir imzacı gereklidir"),
});
export type SetContractSignersInput = z.infer<typeof setContractSignersSchema>;

export const CONTRACT_SIGNER_STATUSES = [
  /** Seri planda sırası gelmemiş imzacı (CLOS-166 `[159]`). */
  "blocked",
  "pending",
  /** Belgeyi açtı ama imzalamadı — imza değil, ilerleme sinyali. */
  "viewed",
  "signed",
  "declined",
  "cancelled",
  "superseded",
  "expired",
  /** Sağlayıcı tarafındaki hata; kanonik terminal durum (CLOS-166 `[159]`). */
  "failed",
] as const;
export type ContractSignerStatus = (typeof CONTRACT_SIGNER_STATUSES)[number];

export const CONTRACT_SIGNER_STATUS_NAMES: Record<ContractSignerStatus, string> = {
  blocked: "Sırası gelmedi",
  pending: "İmza bekleniyor",
  viewed: "Görüntülendi",
  signed: "İmzalandı",
  declined: "Reddedildi",
  cancelled: "İptal edildi",
  superseded: "Yeni versiyon nedeniyle geçersiz",
  expired: "Süresi doldu",
  failed: "Hata",
};

/**
 * İMZACI DURUM KOMUTLARI (CLOS-166 `[159]`).
 *
 * ⚠ Kullanıcı yalnız KENDİ imzacı satırını ilerletebiliyor ve yalnız bu üç geçişi
 * yapabiliyor; `cancelled`/`superseded`/`expired` sistemin vardığı sonuçlar, komut değil.
 */
export const CONTRACT_SIGNER_COMMANDS = ["viewed", "signed", "declined"] as const;
export type ContractSignerCommand = (typeof CONTRACT_SIGNER_COMMANDS)[number];

/**
 * Uç adı → hedef durum. Yol EYLEMİ ("imzala"), durum SONUCU ("imzalandı") anlatıyor;
 * ikisini tek kelimeye sıkıştırsaydık ya uç `/signed` olurdu (emir kipi değil) ya da
 * durum `sign` (durum değil eylem).
 */
export const CONTRACT_SIGNER_COMMAND_ROUTES = [
  ["view", "viewed"],
  ["sign", "signed"],
  ["decline", "declined"],
] as const satisfies readonly (readonly [string, ContractSignerCommand])[];

export const signerTransitionSchema = z.strictObject({
  note: z.string().trim().max(2000, "Gerekçe en fazla 2000 karakter olabilir").optional(),
});
export type SignerTransitionInput = z.infer<typeof signerTransitionSchema>;

/** İmzalı belge arşivi (CLOS-167 `[160]`). */
export type ContractSignatureArtifactSummary = {
  id: string;
  requestId: string;
  contractId: string;
  versionId: string;
  /** İmzalanan versiyonun bütünlük özeti — `[133]` sözleşmesinden kopyalanıyor. */
  originalContentHash: string;
  signedObjectId: string | null;
  signedContentHash: string | null;
  signedAt: string | null;
  attachedByName: string | null;
  createdAt: string;
};

/** Manuel imza komutu (CLOS-167 `[160]`). */
export const recordManualSignatureSchema = z.strictObject({
  method: z.enum(SIGNATURE_METHODS, { message: "Geçerli bir imza yöntemi seçilmelidir" }),
  signedAt: z.iso.datetime({ message: "Geçerli bir imza tarihi girin" }),
  /** Kanıt belgesi; konfig zorunlu kılıyorsa eksikliği reddediliyor. */
  evidenceObjectId: z.uuid("Geçerli bir belge seçilmelidir").optional(),
  note: z.string().trim().max(2000, "Not en fazla 2000 karakter olabilir").optional(),
});
export type RecordManualSignatureInput = z.infer<typeof recordManualSignatureSchema>;

/** İmzalı belgeyi arşive bağlama (CLOS-167 `[160]`). */
export const attachSignedArtifactSchema = z.strictObject({
  storageObjectId: z.uuid("Geçerli bir belge seçilmelidir"),
  /** Verilirse deponun hesapladığı özetle KARŞILAŞTIRILIYOR. */
  expectedContentHash: z.string().trim().min(16).max(200).optional(),
});
export type AttachSignedArtifactInput = z.infer<typeof attachSignedArtifactSchema>;

/**
 * TEKİL İMZACI İPTALİ/YENİDEN ATAMA (CLOS-233 `[170]` DEV-174 — CLOS-922 ortak çekirdek /
 * CLOS-926 adapter).
 *
 * ⚠ `signerTransitionSchema` DEĞİL: bu komutlar imzacının KENDİ ilerlettiği `view/sign/
 * decline` üçlüsünden ayrı — burada yetkili bir kullanıcı BAŞKA birinin görevini yönetiyor
 * (`CONTRACT_SIGNER_COMMANDS`'ın kasıtlı olarak DIŞINDA bıraktığı "sistemin vardığı
 * sonuçlar" ailesi).
 */
export const cancelSignatureRequestSignerSchema = z.strictObject({
  reason: z.string().trim().max(2000, "Gerekçe en fazla 2000 karakter olabilir").optional(),
});
export type CancelSignatureRequestSignerInput = z.infer<typeof cancelSignatureRequestSignerSchema>;

/**
 * ⚠ Yeniden atama YALNIZ `open` (gönderilmemiş) talepte kabul edilir — gönderilmiş talep
 * için servis kanonik "iptal + yeni talep" yoluna yönlendirir (kartın kuralı).
 */
export const reassignSignatureRequestSignerSchema = z.strictObject({
  newSignerUserId: z.uuid("Geçerli bir kullanıcı seçilmelidir"),
  newSignerTenantId: z.uuid("Geçerli bir çalışma alanı seçilmelidir"),
  reason: z.string().trim().max(2000, "Gerekçe en fazla 2000 karakter olabilir").optional(),
});
export type ReassignSignatureRequestSignerInput = z.infer<
  typeof reassignSignatureRequestSignerSchema
>;

export type ContractSignerEventSummary = {
  id: string;
  signerId: string | null;
  signerName: string | null;
  source: "user" | "provider" | "system" | "manual";
  actorName: string | null;
  fromStatus: string;
  toStatus: string;
  note: string | null;
  occurredAt: string;
};

export type ContractSignerSummary = {
  id: string;
  partyId: string;
  partyName: string;
  signerUserId: string;
  signerName: string;
  signerTenantId: string;
  position: number;
  required: boolean;
  status: ContractSignerStatus;
  resolvedAt: string | null;
};
export type CreateSignatureRequestInput = z.infer<typeof createSignatureRequestSchema>;

export const CONTRACT_SIGNATURE_REQUEST_STATUSES = [
  "open",
  /** Plan kilitlendi, imzalar toplanıyor (CLOS-165 `[158]`). */
  "sent",
  "cancelled",
  "superseded",
  /** ⚠ Yalnız BÜTÜN ZORUNLU imzacılar imzaladığında (CLOS-166 `[159]`). */
  "completed",
  /** Zorunlu bir imzacı reddetti. */
  "declined",
  "expired",
  /** Sağlayıcı hatası — terminal (CLOS-166 `[159]`). */
  "failed",
] as const;
export type ContractSignatureRequestStatus = (typeof CONTRACT_SIGNATURE_REQUEST_STATUSES)[number];

export const CONTRACT_SIGNATURE_REQUEST_STATUS_NAMES: Record<
  ContractSignatureRequestStatus,
  string
> = {
  open: "İmzacılar planlanıyor",
  sent: "İmza bekleniyor",
  cancelled: "İptal edildi",
  superseded: "Yeni versiyon nedeniyle geçersiz",
  completed: "İmza tamamlandı",
  declined: "İmza reddedildi",
  expired: "Süresi doldu",
  failed: "Hata",
};

export type ContractSignatureRequestSummary = {
  id: string;
  contractId: string;
  versionId: string;
  versionNumber: number;
  eligibilityPolicy: SignatureEligibilityPolicy;
  evidenceId: string | null;
  signingMode: SigningMode;
  status: ContractSignatureRequestStatus;
  description: string | null;
  startedByName: string;
  expiresAt: string | null;
  createdAt: string;
  signers: ContractSignerSummary[];
};
